import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { authService } from '../authService';
import { API_BASE_URL } from '../../config/api';

/**
 * API Client Configuration
 * Centralized axios instance with interceptors
 */

// Singleton promise: ensures concurrent 401/403 retries share one token refresh
// instead of each calling getAccessToken() simultaneously (race condition).
let _refreshPromise: Promise<string | null> | null = null;
function getOrRefreshToken(): Promise<string | null> {
  if (!_refreshPromise) {
    _refreshPromise = authService.getAccessToken().finally(() => {
      _refreshPromise = null;
    });
  }
  return _refreshPromise;
}

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  // CN-3: was 30000. A slow save that actually completes server-side after 30s previously got
  // aborted client-side first, showing a false "can't reach server" error even though the DB was
  // updated. 60s is a safety net while the server-side scoping fix keeps normal saves near-instant.
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});


/**
 * Request Interceptor
 * Attach a bearer token (Graph access token) to every request.
 * If no token is in localStorage, acquire one from MSAL first.
 * The token may be either a JWT or an opaque Graph access token — the backend
 * validates it against Microsoft Graph either way, so we don't try to parse it.
 *
 * In development builds, also injects X-Dev-* headers from the DevTestPanel
 * localStorage state so the backend DevBypassMiddleware can impersonate users
 * and switch databases without restarting the server.
 */
apiClient.interceptors.request.use(
  async (config) => {
    // ── Normal auth token attachment ───────────────────────────────────────
    let token = sessionStorage.getItem('token');
    if (!token) {
      await authService.getAccessToken().catch(() => null);
      token = sessionStorage.getItem('token');
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('[API] No token available — request will be sent without Authorization header');
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor
 * Handle responses and errors globally
 */
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Return data directly
    return response.data;
  },
  async (error) => {
    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.message || error.message;
      const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

      switch (status) {
        case 401:
          if (!originalRequest._retry) {
            // First 401 — token may be expired. Clear and refresh via shared promise
            // so concurrent requests don't each trigger a separate getAccessToken() call.
            originalRequest._retry = true;
            sessionStorage.removeItem('token');
            try {
              await getOrRefreshToken();
              const newToken = sessionStorage.getItem('token');
              if (newToken) {
                (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
                return apiClient(originalRequest);
              }
            } catch {
              // Silent refresh failed — fall through and reject
            }
          }
          console.warn('[API] 401 Unauthorized — token refresh failed or retry exhausted.');
          break;
        case 403:
          // 403 can also mean the Graph token expired (backend auth handler returns Fail which
          // ASP.NET Core may surface as 403 instead of 401 in some cases). Attempt one token
          // refresh before treating it as a true access-denied.
          if (!originalRequest._retry) {
            originalRequest._retry = true;
            sessionStorage.removeItem('token');
            try {
              await getOrRefreshToken();
              const newToken = sessionStorage.getItem('token');
              if (newToken) {
                (originalRequest.headers as Record<string, string>).Authorization = `Bearer ${newToken}`;
                return apiClient(originalRequest);
              }
            } catch {
              // Refresh failed — this is a genuine 403
            }
          }
          console.error('[API] 403 Forbidden — access denied after token refresh attempt:', message);
          break;
        case 404:
          // Not found
          console.error('Resource not found:', message);
          break;
        case 500:
          // Server error
          console.error('Server error:', message);
          break;
        default:
          console.error('API error:', message);
      }

      return Promise.reject({
        status,
        message,
        data: error.response.data,
      });
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server', error.request);
      return Promise.reject({
        message: 'Network error. Please check your connection.',
      });
    } else {
      // Error in request setup
      console.error('Request error:', error.message);
      return Promise.reject({
        message: error.message,
      });
    }
  }
);

/**
 * Generic request wrapper
 */
export const apiRequest = {
  get: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.get(url, config);
  },
  post: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.post(url, data, config);
  },
  put: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.put(url, data, config);
  },
  patch: <T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.patch(url, data, config);
  },
  delete: <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
    return apiClient.delete(url, config);
  },
};

export default apiClient;
